import pDebounce from 'p-debounce';
import React, {
  PropsWithChildren,
  useCallback,
  useContext,
  useMemo,
  useState,
} from 'react';

import {
  updatePersonalConfiguration,
  updateSyncConfiguration,
} from '../services/api';
import { getErrorMessage } from '../services/helpers';
import { showFlag } from '../services/jira';
import { PersonalConfiguration } from '../types';
import { useDatabaseContext } from './DatabaseContextProvider';

interface PersonalConfigurationContextData {
  updateConfig: (
    config: Partial<PersonalConfiguration>,
    shouldShowFlag?: boolean
  ) => Promise<void>;
  config: PersonalConfiguration;
}

const PersonalConfigurationContext = React.createContext<
  PersonalConfigurationContextData | undefined
>(undefined);

const debouncedUpdatePersonalConfiguration = pDebounce(
  async (config: Partial<PersonalConfiguration>, shouldShowFlag: boolean) => {
    if (Object.keys(config).includes('syncByCron')) {
      await updateSyncConfiguration(config);
    } else {
      await updatePersonalConfiguration(config);
    }
    if (shouldShowFlag) {
      showFlag('Configuration saved', '', 'success');
    }
  },
  500
);

export function PersonalConfigurationProvider({
  children,
}: Readonly<PropsWithChildren>) {
  const {
    data: { personalConfiguration },
    updateContext,
  } = useDatabaseContext();
  const [confirmedConfig, setConfirmedConfig] = useState<PersonalConfiguration>(
    personalConfiguration
  );

  const setConfig = useMemo(
    () => updateContext('personalConfiguration'),
    [updateContext]
  );

  const updateConfig = useCallback(
    async (
      newConfig: Partial<PersonalConfiguration>,
      shouldShowFlag = true
    ) => {
      const oldConfig = { ...personalConfiguration };
      const updatedConfig = { ...oldConfig, ...newConfig };
      try {
        setConfig(updatedConfig);
        await debouncedUpdatePersonalConfiguration(newConfig, shouldShowFlag);
        setConfirmedConfig(updatedConfig);
      } catch (error) {
        setConfig(confirmedConfig);
        if (shouldShowFlag) {
          showFlag(
            'Error while saving personal configuration',
            getErrorMessage(error),
            'error'
          );
        }
      }
    },
    [personalConfiguration, setConfig]
  );

  const context = useMemo(
    () => ({
      config: personalConfiguration,
      updateConfig,
    }),
    [personalConfiguration, updateConfig]
  );

  return (
    <PersonalConfigurationContext.Provider value={context}>
      {children}
    </PersonalConfigurationContext.Provider>
  );
}

export function usePersonalConfiguration() {
  const context = useContext(PersonalConfigurationContext);
  if (!context) {
    throw new Error(
      'Personal Configuration context not initialized, probably context provider is missing'
    );
  }
  return context;
}
